// @file Pinia store for the starting state of the surface
import { PadletStartingStateApi } from '@@/surface/api/padlet_starting_state'
import type { Library, OnboardingWallViz, User, WallViz } from '@@/types'
import { blankState } from '@@/vuexstore/surface'
import type { SurfaceState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Library data returned in the surface starting state. See surface_controller.rb.
 * This is a minimal subset of the Library type.
 *
 * Fields vary by API version:
 * - v10: No walls_used or makers_count
 * - v7/v8/legacy: Includes walls_used and makers_count
 */
export interface StartingStateLibrary {
  id?: Library['id']
  hashid?: Library['hashid']
  name?: Library['name']
  slug?: Library['slug']
  libraryType?: Library['libraryType']
  dashboard_url?: string // Starting state API returns snake_case, Library type has dashboardUrl
  isDeprecatedFreeTier?: Library['isDeprecatedFreeTier']
  isArchiveEnabled?: Library['isArchiveEnabled']
  // Legacy fields not present in starting state API v10+
  walls_used?: number
  makers_count?: number
}

export const useSurfaceStartingStateStore = defineStore('surfaceStartingState', () => {
  const startingState = ref<Omit<SurfaceState, 'library'> & { library: StartingStateLibrary }>({ ...blankState() })

  const arvoConfig = computed(() => startingState.value.arvoConfig)
  const googleApiKeyForMap = computed<string>(() => startingState.value.constants.googleApiKeyForMap)
  const currentCountryCode = computed<string>(() => startingState.value.constants.currentCountryCode)
  const currentCountry = computed<string>(() => startingState.value.constants.currentCountry)
  const morpheusToken = computed<string>(() => startingState.value.constants.morpheusToken)
  const canMakeWallInTenant = computed<boolean>(() => startingState.value.canMakeWallInTenant)
  const user = computed<User>(() => startingState.value.user)

  // #region wall layouts
  const allLayouts = computed<OnboardingWallViz[]>(() => {
    const layouts = startingState.value.constants.allLayouts as OnboardingWallViz[]
    return layouts
  })

  const allLayoutsByGroup = computed(() => {
    const groups = startingState.value.constants.allLayoutsByGroup

    return {
      simple: groups.simple,
      advanced: groups.advanced,
    }
  })

  const sectionableLayouts = computed<WallViz[]>(() => {
    // some pages like mobile wish frames use this store without the full starting state like surface so some data is missing
    // they don't need this data anyways so just return an empty array for now
    const sectionableLayouts: WallViz[] | undefined = startingState.value.constants.sectionableLayouts
    if (sectionableLayouts == null) return []
    return sectionableLayouts
  })
  // #endregion

  function intializeState(state: SurfaceState): void {
    startingState.value = state
  }

  async function fetchStartingState(): Promise<void> {
    const response = await PadletStartingStateApi.fetch()
    startingState.value = Object.assign(startingState.value, response)
  }

  return {
    startingState,
    arvoConfig,
    googleApiKeyForMap,
    currentCountryCode,
    currentCountry,
    morpheusToken,
    canMakeWallInTenant,
    intializeState,
    fetchStartingState,
    user,

    // Layouts
    allLayouts,
    allLayoutsByGroup,
    sectionableLayouts,
  }
})
